import React, { useState, useEffect, useRef } from 'react';
import '../styles/WorkoutPlayer.css';
import { api } from '../services/api';

// Helper to determine if we're in development mode
const isDev = process.env.NODE_ENV === 'development';

// LocalStorage key for workout state
const WORKOUT_STATE_KEY = 'workout-player-state';

const calculatePlates = (totalWeight) => {
  const barWeight = 45;
  const availablePlates = [45, 25, 10, 5, 2.5];
  const plates = {};

  if (totalWeight <= barWeight) {
    return []; // Only the bar is needed
  }

  let weightPerSide = (totalWeight - barWeight) / 2;

  if (weightPerSide < 0) return []; // Weight is less than bar weight

  availablePlates.forEach(plateWeight => {
    const count = Math.floor(weightPerSide / plateWeight);
    if (count > 0) {
      plates[plateWeight] = count;
      weightPerSide -= count * plateWeight;
    }
  });

  // Convert plates object to an array of plate weights for rendering
  const plateList = [];
  Object.entries(plates).forEach(([weight, count]) => {
    for (let i = 0; i < count; i++) {
      plateList.push(parseFloat(weight));
    }
  });
  
  // Sort plates visually largest to smallest (closest to bar)
  plateList.sort((a, b) => b - a);

  return plateList;
};

// Empty bar component - just shows the barbell without any plates
const EmptyBar = ({ side, hideBar = false }) => (
  <div className={`plate-stack plate-stack-${side}`} style={{ '--barbell-opacity': hideBar ? 0 : 1 }}>
    <div className={`barbell-bar barbell-bar-${side}`}></div>
    {/* No plates or text - just the bar itself */}
  </div>
);

// Component to render plates with weights and a bar
const PlateDisplay = ({ plates, side = 'right', hideBar = false }) => {
  return (
    <div className={`plate-stack plate-stack-${side}`} style={{ '--barbell-opacity': hideBar ? 0 : 1 }}>
      {/* Simple bar that goes under the plates */}
      <div className={`barbell-bar barbell-bar-${side}`}></div>
      
      {/* Plates */}
      {(side === 'left' ? [...plates].reverse() : plates).map((plate, index) => (
        <div key={index} className={`plate plate-${String(plate).replace('.', '-')}`} title={`${plate} lb plate`}>
          <span className="plate-weight">{plate}</span>
        </div>
      ))}
    </div>
  );
};

// Component to render completion confirmation UI
const CompletionConfirmation = ({ exercise, weight, reps, sets, onComplete }) => (
  <div className="completion-confirmation">
    <p className="completion-question">
      Did you complete all {sets} × {reps} reps of {exercise} at {weight} lbs?
    </p>
    <div className="completion-actions">
      <button 
        className="completion-button" 
        onClick={() => onComplete(true)}
        aria-label="Yes, completed all reps"
      >
        <svg viewBox="0 0 24 24" width="100%" height="100%" className="check-icon">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
      </button>
      <button 
        className="completion-button" 
        onClick={() => onComplete(false)}
        aria-label="No, didn't complete all reps"
      >
        <svg viewBox="0 0 24 24" width="100%" height="100%" className="x-icon">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>
  </div>
);

const WorkoutPlayer = ({ workout, onWorkoutComplete }) => {
  // Read stored workout state from localStorage if it exists and matches current workout
  const getInitialState = () => {
    try {
      const savedState = JSON.parse(localStorage.getItem(WORKOUT_STATE_KEY));
      
      // Check if saved state exists and matches current workout
      if (savedState && savedState.workoutName === workout.name) {
        console.log('Restored workout state from localStorage');
        return {
          currentExerciseIndex: savedState.currentExerciseIndex,
          currentSet: savedState.currentSet,
          isResting: savedState.isResting,
          secondsLeft: savedState.secondsLeft,
          isPlaying: false, // Always start paused when restoring, even if it was playing
          showCompletionConfirmation: savedState.showCompletionConfirmation,
          workoutFinished: savedState.workoutFinished,
          exerciseData: savedState.exerciseData || workout.exercises.map(ex => ({
            name: ex.name,
            currentWeight: ex.isBarbell ? Math.max(45, ex.startWeight) : ex.startWeight,
            completed: false,
            completedSuccessfully: null,
            history: []
          }))
        };
      }
    } catch (error) {
      console.error('Error restoring workout state:', error);
    }
    
    // Default state if no saved state or error
    return {
      currentExerciseIndex: 0,
      currentSet: 1,
      isResting: false,
      secondsLeft: 0,
      isPlaying: false,
      showCompletionConfirmation: false,
      workoutFinished: false,
      exerciseData: workout.exercises.map(ex => ({
        name: ex.name,
        currentWeight: ex.isBarbell ? Math.max(45, ex.startWeight) : ex.startWeight,
        completed: false,
        completedSuccessfully: null,
        history: []
      }))
    };
  };

  // Initialize state from localStorage if available
  const initialState = getInitialState();
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(initialState.currentExerciseIndex);
  const [currentSet, setCurrentSet] = useState(initialState.currentSet);
  const [isResting, setIsResting] = useState(initialState.isResting);
  const [secondsLeft, setSecondsLeft] = useState(initialState.secondsLeft);
  const [isPlaying, setIsPlaying] = useState(initialState.isPlaying);
  const [showCompletionConfirmation, setShowCompletionConfirmation] = useState(initialState.showCompletionConfirmation);
  const [workoutFinished, setWorkoutFinished] = useState(initialState.workoutFinished);
  const [estimatedEndTime, setEstimatedEndTime] = useState(null);
  const [exerciseData, setExerciseData] = useState(initialState.exerciseData);
  
  // Reset exercise data only when workout name changes
  useEffect(() => {
    // Check if we need to reset (different workout)
    const savedState = JSON.parse(localStorage.getItem(WORKOUT_STATE_KEY) || '{}');
    if (!savedState.workoutName || savedState.workoutName !== workout.name) {
      setExerciseData(workout.exercises.map(ex => ({
        name: ex.name,
        currentWeight: ex.isBarbell ? Math.max(45, ex.startWeight) : ex.startWeight,
        completed: false,
        completedSuccessfully: null,
        history: []
      })));
      setCurrentExerciseIndex(0);
      setCurrentSet(1);
      setIsResting(false);
      setSecondsLeft(0);
      setIsPlaying(false);
      setShowCompletionConfirmation(false);
      setWorkoutFinished(false);
    }
  }, [workout.name]);
  
  // Load exercise history when current exercise changes
  useEffect(() => {
    const loadExerciseHistory = async () => {
      if (currentExerciseIndex >= 0 && currentExerciseIndex < workout.exercises.length) {
        const exerciseName = workout.exercises[currentExerciseIndex].name;
        try {
          const history = await api.getExerciseHistory(exerciseName);
          
          // Update exercise data with history
          const updatedExerciseData = [...exerciseData];
          updatedExerciseData[currentExerciseIndex].history = history;
          
          // If there's history, handle weight based on last attempt
          if (history.length > 0) {
            // Sort history to get the most recent record first
            const sortedHistory = [...history].sort((a, b) => 
              new Date(b.date) - new Date(a.date)
            );
            
            const lastRecord = sortedHistory[0];
            
            if (lastRecord) {
              const minWeight = currentExercise.isBarbell ? 45 : 0;
              
              if (lastRecord.wasSuccessful) {
                // If the last attempt was successful, add the increment
                updatedExerciseData[currentExerciseIndex].currentWeight = 
                  Math.max(minWeight, lastRecord.weight + currentExercise.increment);
              } else {
                // If the last attempt was a failure, use the same weight
                updatedExerciseData[currentExerciseIndex].currentWeight = 
                  Math.max(minWeight, lastRecord.weight);
              }
            }
          }
          
          setExerciseData(updatedExerciseData);
        } catch (error) {
          console.error(`Failed to load history for ${exerciseName}:`, error);
          // Continue without history if fetch fails
        }
      }
    };
    
    loadExerciseHistory();
  }, [currentExerciseIndex, workout.exercises]);
  
  // Handle workout completion
  useEffect(() => {
    if (workoutFinished && onWorkoutComplete) {
      // Clear saved state from localStorage when workout is complete
      localStorage.removeItem(WORKOUT_STATE_KEY);
      
      // Immediately move to the next workout
      onWorkoutComplete();
    }
  }, [workoutFinished, onWorkoutComplete]);
  
  const timerRef = useRef(null);

  const currentExercise = workout.exercises[currentExerciseIndex];
  const currentExerciseData = exerciseData[currentExerciseIndex];


  // Effect for handling timer and set progression
  useEffect(() => {
    if (isPlaying && secondsLeft > 0) {
      timerRef.current = setTimeout(() => {
        setSecondsLeft(prev => prev - 1);
      }, 1000);
    } else if (isPlaying && secondsLeft === 0) {
      if (isResting) {
        // Rest period is over, move to the next set or exercise
        setIsResting(false);
        if (currentSet === currentExercise.sets) {
          // All sets completed for this exercise, show completion confirmation
          setIsPlaying(false);
          setShowCompletionConfirmation(true);
        } else {
          // Move to next set
          setCurrentSet(currentSet + 1);
        }
      } else {
        // Set completed, start rest period
        setIsResting(true);
        setSecondsLeft(currentExercise.restSeconds);
      }
    }

    return () => clearTimeout(timerRef.current);
  }, [isPlaying, secondsLeft, isResting, currentSet, currentExercise, currentExerciseIndex, workout.exercises.length]);
  
  // Effect for automatically starting the timer for the next exercise
  useEffect(() => {
    // Automatically start the timer when entering a new exercise (except for the first exercise)
    if (currentExerciseIndex > 0 && !isResting && secondsLeft === 0 && !isPlaying && !showCompletionConfirmation) {
      setIsPlaying(true);
    }
  }, [currentExerciseIndex, isResting, secondsLeft, isPlaying, showCompletionConfirmation]);
  
  // Effect to update the estimated end time
  useEffect(() => {
    if (!workoutFinished) {
      const endTime = calculateEstimatedEndTime();
      setEstimatedEndTime(endTime);
    }
  }, [currentExerciseIndex, currentSet, secondsLeft, isResting, isPlaying, showCompletionConfirmation, workoutFinished]);
  
  // Effect to save workout state to localStorage
  useEffect(() => {
    const workoutState = {
      workoutName: workout.name,
      currentExerciseIndex,
      currentSet,
      isResting,
      secondsLeft,
      isPlaying,
      showCompletionConfirmation,
      workoutFinished,
      exerciseData
    };
    
    localStorage.setItem(WORKOUT_STATE_KEY, JSON.stringify(workoutState));
  }, [
    workout.name,
    currentExerciseIndex,
    currentSet,
    isResting,
    secondsLeft,
    isPlaying,
    showCompletionConfirmation,
    workoutFinished,
    exerciseData
  ]);
  

  const togglePlayPause = () => {    
    if (!isPlaying && secondsLeft === 0 && !isResting) {
      // Starting a new set
      setIsPlaying(true);
    } else {
      setIsPlaying(!isPlaying);
    }
  };
  
  const fastForward = () => {
    // Skip to the end of the current timer
    setSecondsLeft(0);
  };

  const resetWorkout = () => {
    setCurrentExerciseIndex(0);
    setCurrentSet(1);
    setIsResting(false);
    setSecondsLeft(0);
    setIsPlaying(false);
    setShowCompletionConfirmation(false);
    
    // Clear saved state from localStorage
    localStorage.removeItem(WORKOUT_STATE_KEY);
  };
  
  const incrementWeight = () => {
    const updatedExerciseData = [...exerciseData];
    updatedExerciseData[currentExerciseIndex].currentWeight += currentExercise.increment;
    setExerciseData(updatedExerciseData);
  };
  
  const decrementWeight = () => {
    const updatedExerciseData = [...exerciseData];
    const minWeight = currentExercise.isBarbell ? 45 : 0; // Minimum weight is 45 lbs for barbell exercises
    updatedExerciseData[currentExerciseIndex].currentWeight = 
      Math.max(minWeight, updatedExerciseData[currentExerciseIndex].currentWeight - currentExercise.increment);
    setExerciseData(updatedExerciseData);
  };
  
  const handleCompletionConfirmation = async (wasSuccessful) => {
    const updatedExerciseData = [...exerciseData];
    updatedExerciseData[currentExerciseIndex].completed = true;
    updatedExerciseData[currentExerciseIndex].completedSuccessfully = wasSuccessful;
    setExerciseData(updatedExerciseData);
    setShowCompletionConfirmation(false);
    
    try {
      // Record this exercise completion in the database
      await api.recordWorkout({
        workoutName: workout.name,
        date: new Date().toISOString(),
        exercises: [{
          name: currentExercise.name,
          weight: updatedExerciseData[currentExerciseIndex].currentWeight,
          reps: currentExercise.reps,
          sets: currentExercise.sets,
          wasSuccessful: wasSuccessful
        }]
      });
    } catch (error) {
      console.error('Failed to record exercise completion:', error);
      // Continue with the workout even if saving fails
    }
    
    // Move to next exercise or end workout
    if (currentExerciseIndex < workout.exercises.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
      setCurrentSet(1);
      // Automatically start rest timer for the next exercise
      setIsPlaying(true);
    } else {
      // Signal workout complete to parent component
      setWorkoutFinished(true);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  
  // Format the end time to display hours and minutes
  const formatEndTime = (date) => {
    if (!date) return '';
    
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM
    
    return `${formattedHours}:${minutes < 10 ? '0' : ''}${minutes} ${ampm}`;
  };

  // Calculate how long the workout will take to finish
  const calculateEstimatedEndTime = () => {
    if (workoutFinished) return null;
    
    // Calculate remaining time in current exercise/rest period
    let totalTimeRemaining = secondsLeft;
    
    // Current exercise remaining sets
    const currentExercise = workout.exercises[currentExerciseIndex];
    const remainingSetsInCurrentExercise = currentExercise.sets - currentSet;
    
    // If we're not in a rest period, we need to add the rest time for current set
    if (!isResting && !showCompletionConfirmation) {
      totalTimeRemaining += currentExercise.restSeconds;
    }
    
    // Add time for remaining sets in current exercise
    totalTimeRemaining += remainingSetsInCurrentExercise * (currentExercise.restSeconds);
    
    // Add time for remaining exercises
    for (let i = currentExerciseIndex + 1; i < workout.exercises.length; i++) {
      const ex = workout.exercises[i];
      totalTimeRemaining += ex.sets * ex.restSeconds;
    }
    
    // Create estimated end time
    const endTime = new Date();
    // If paused, we need to account for the fact that the timer isn't running
    if (!isPlaying) {
      // Add a buffer time to account for the workout being paused
      // 30 seconds per exercise set as a rough estimate for exercise completion
      const completionTimeBuffer = workout.exercises.reduce((total, ex, idx) => {
        // Only count remaining sets in current exercise and all sets in future exercises
        if (idx === currentExerciseIndex) {
          return total + (currentExercise.sets - currentSet + 1) * 30;
        } else if (idx > currentExerciseIndex) {
          return total + ex.sets * 30;
        }
        return total;
      }, 0);
      
      totalTimeRemaining += completionTimeBuffer;
    }
    
    endTime.setSeconds(endTime.getSeconds() + totalTimeRemaining);
    
    return endTime;
  };



  return (
    <div className="workout-player">
      <div className="exercise-info">
        <h2>{currentExercise.name}</h2>
        <div className="weight-control">
          <button className="weight-button" onClick={decrementWeight}>-</button>
          <span className="weight">
            {currentExerciseData.currentWeight} lbs
          </span>
          <button className="weight-button" onClick={incrementWeight}>+</button>
        </div>
        <div className={`reps-and-plates-container ${!currentExercise.isBarbell ? 'centered-reps' : ''}`}>
            <div className="barbell-container">
              {/* Always show plates for visual consistency, but hide barbell for non-barbell exercises */}
              {currentExercise.isBarbell ? (
                currentExerciseData.currentWeight === 45 ? (
                  <EmptyBar side="left" />
                ) : (
                  <PlateDisplay 
                    plates={calculatePlates(currentExerciseData.currentWeight)} 
                    side="left" 
                  />
                )
              ) : (
                <PlateDisplay 
                  plates={calculatePlates(currentExerciseData.currentWeight)} 
                  side="left"
                  hideBar={true}
                />
              )}
              <div className="reps-display">
                <p className="reps-count">{currentExercise.reps}</p>
                <p className="reps-label">REPS</p>
              </div>
              {currentExercise.isBarbell ? (
                currentExerciseData.currentWeight === 45 ? (
                  <EmptyBar side="right" />
                ) : (
                  <PlateDisplay 
                    plates={calculatePlates(currentExerciseData.currentWeight)} 
                    side="right" 
                  />
                )
              ) : (
                <PlateDisplay 
                  plates={calculatePlates(currentExerciseData.currentWeight)} 
                  side="right"
                  hideBar={true} 
                />
              )}
            </div>
        </div>
        
        <div className="set-info">
          <div className="set-squares">
            {[...Array(currentExercise.sets)].map((_, index) => (
              <div 
                key={index} 
                className={`set-square ${index + 1 <= currentSet - 1 || (index + 1 === currentExercise.sets && showCompletionConfirmation) ? 'filled' : ''}`} 
              />
            ))}
          </div>
        </div>
        
        {showCompletionConfirmation ? (
          <CompletionConfirmation 
            exercise={currentExercise.name}
            weight={currentExerciseData.currentWeight}
            reps={currentExercise.reps}
            sets={currentExercise.sets}
            onComplete={handleCompletionConfirmation}
          />
        ) : (
          secondsLeft > 0 && (
            <p className="timer">
              {secondsLeft}
            </p>
          )
        )}
        
        <p className="notes">{currentExercise.notes}</p>
      </div>
      
      <div className="controls">
        {!showCompletionConfirmation && (
          <>
            <button 
              className={`control-button ${isPlaying ? 'pause' : 'play'}`} 
              onClick={togglePlayPause}
            >
              {isPlaying ? 'Pause' : 'Play'}
            </button>
            {isDev && (
              <button 
                className="control-button fast-forward" 
                onClick={fastForward}
                title="Fast Forward (development only)"
              >
                ⏩
              </button>
            )}
            {estimatedEndTime && (
              <div className="estimated-end-time">
                Finish time: {formatEndTime(estimatedEndTime)}
              </div>
            )}
          </>
        )}
      </div>
      
      <div className="workout-progress">
        <h3>Workout Progress</h3>
        <div className="exercise-list">
          {workout.exercises.map((exercise, index) => (
            <div 
              key={index} 
              className={`exercise-item ${index === currentExerciseIndex ? 'current' : ''} ${exerciseData[index].completed ? 'completed' : ''}`}
            >
              {exercise.name}
              {exerciseData[index].completed && (
                <span className="completion-status">
                  {exerciseData[index].completedSuccessfully ? (
                    <svg viewBox="0 0 24 24" width="24" height="24" className="exercise-icon check-icon">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="24" height="24" className="exercise-icon x-icon">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  )}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorkoutPlayer;